import {
  BUILT_IN_ISSUE_STATUSES,
  type IssueStatus,
  type IssueStatusCategory,
  type PMIssueStatus,
} from "@/domains/pm/types";
import { ISSUE_STATUS_CONFIG } from "@/domains/pm/constants";

/**
 * Everything a surface needs to draw and reason about one status, resolved.
 *
 * This replaces four things that used to be separate and could disagree:
 * `ISSUE_STATUS_CONFIG` (colours), `t.pm.issues.status[key]` (labels), the
 * hard-coded orderings in `ISSUE_STATUSES` / `KanbanBoard.COLUMN_ORDER` /
 * `groupAndSort.STATUS_ORDER` / `protocol.STATUS_ORDER`, and the literal
 * `status === "in_progress"` test for "is this live work".
 */
export interface StatusDescriptor {
  key: IssueStatus;
  label: string;
  category: IssueStatusCategory;
  dotColor: string;
  bgColor: string;
  textColor: string;
  sortOrder: number;
  /** Built-ins cannot be renamed, recoloured, reordered, hidden or deleted. */
  custom: boolean;
  /** The project that defined it. `null` for a built-in. */
  buildingProjectId: number | null;
}

export interface StatusCatalogue {
  /** The three built-ins, in workflow order. */
  builtIns: StatusDescriptor[];
  /** Custom descriptors keyed by `${buildingProjectId}:${key}`. */
  custom: Map<string, StatusDescriptor>;
}

/** Built-ins sort before every custom status, in workflow order. */
const BUILT_IN_CATEGORY: Record<string, IssueStatusCategory> = {
  in_progress: "open",
  resolved: "done",
  closed: "done",
};

/**
 * What an unknown key resolves to. It is reachable in normal use — a status
 * row can be deleted while a page is open, and a multi-project page can show
 * an issue whose status belongs to a project the user cannot read — so every
 * lookup returns something renderable rather than throwing on `.dotColor`.
 */
function unknownDescriptor(key: IssueStatus): StatusDescriptor {
  return {
    key,
    label: key,
    category: "open",
    dotColor: "#9e9e9e",
    bgColor: "#f5f5f5",
    textColor: "#616161",
    sortOrder: Number.MAX_SAFE_INTEGER,
    custom: true,
    buildingProjectId: null,
  };
}

const customKey = (projectId: number, key: string) => `${projectId}:${key}`;

/**
 * Where the three built-ins sit on the shared ordering axis.
 *
 * Spaced rather than adjacent so a custom status can be dropped BETWEEN two of
 * them. The built-ins are never written and never reordered among themselves:
 * In Progress → Resolved → Closed is fixed, and everything else finds a place
 * around it.
 *
 * NEGATIVE, and that is the important part. Every custom row written before
 * this ordering existed carries a small non-negative `sort_order` (0, 1, 2 —
 * `takenKeys.length` at the time it was created), and those rows meant "after
 * the built-ins", because the old code offset them past. Anchoring the
 * built-ins below zero keeps that true without rewriting a single row, so no
 * existing board rearranges itself the day this ships.
 */
export const BUILT_IN_SORT_STEP = 100;
export const BUILT_IN_ANCHOR_START = -300;

export function buildStatusCatalogue(rows: PMIssueStatus[], language: string): StatusCatalogue {
  const et = language === "et";

  const builtIns = BUILT_IN_ISSUE_STATUSES.map((key, index) => {
    const cfg = ISSUE_STATUS_CONFIG[key];
    return {
      key,
      label: et ? cfg.label_et : cfg.label_en,
      category: BUILT_IN_CATEGORY[key],
      dotColor: cfg.dotColor,
      bgColor: cfg.bgColor,
      textColor: cfg.textColor,
      sortOrder: BUILT_IN_ANCHOR_START + index * BUILT_IN_SORT_STEP,
      custom: false,
      buildingProjectId: null,
    } satisfies StatusDescriptor;
  });

  const custom = new Map<string, StatusDescriptor>();
  for (const row of rows) {
    custom.set(customKey(row.building_project_id, row.key), {
      key: row.key,
      // A blank Estonian label is worse than an English one.
      label: et ? row.label_et?.trim() || row.label_en : row.label_en,
      category: row.category,
      dotColor: row.dot_color,
      bgColor: row.bg_color,
      textColor: row.text_color,
      // The SAME axis the built-ins sit on, so a custom status can precede
      // one. It used to be offset past them, which pinned every custom column
      // to the right-hand end of the board whatever its sort_order said.
      sortOrder: row.sort_order,
      custom: true,
      buildingProjectId: row.building_project_id,
    });
  }

  return { builtIns, custom };
}

export const EMPTY_STATUS_CATALOGUE: StatusCatalogue = {
  builtIns: buildStatusCatalogue([], "en").builtIns,
  custom: new Map(),
};

/**
 * Resolve one key. `projectId` is the issue's building project; pass null when
 * the caller genuinely has no project in hand, and the key is matched against
 * every project's customs (first by sort order wins).
 */
export function resolveStatus(
  catalogue: StatusCatalogue,
  projectId: number | null | undefined,
  key: IssueStatus
): StatusDescriptor {
  const builtIn = catalogue.builtIns.find((d) => d.key === key);
  if (builtIn) return builtIn;

  if (projectId != null) {
    const exact = catalogue.custom.get(customKey(projectId, key));
    if (exact) return exact;
    return unknownDescriptor(key);
  }

  let best: StatusDescriptor | undefined;
  for (const d of catalogue.custom.values()) {
    if (d.key !== key) continue;
    if (!best || d.sortOrder < best.sortOrder) best = d;
  }
  return best ?? unknownDescriptor(key);
}

/**
 * The ordered statuses a surface should offer: the built-ins and the customs of
 * `projectId`, merged into ONE order. With no project (the "All projects" view)
 * the customs are the union of every project's, deduplicated by key — two
 * projects may both define "blocked", and the board must not grow two columns
 * for it.
 *
 * Built-ins and customs share one axis, so a custom status can sit anywhere —
 * including before In Progress. A tie goes to the built-in, which is what keeps
 * the three of them in their own order when a custom lands exactly on one.
 */
export function visibleStatuses(
  catalogue: StatusCatalogue,
  projectId: number | null | undefined
): StatusDescriptor[] {
  const customs: StatusDescriptor[] = [];
  const seen = new Set<string>();
  for (const d of catalogue.custom.values()) {
    if (projectId != null && d.buildingProjectId !== projectId) continue;
    if (seen.has(d.key)) continue;
    seen.add(d.key);
    customs.push(d);
  }
  return [...catalogue.builtIns, ...customs].sort(
    (a, b) =>
      a.sortOrder - b.sortOrder || Number(a.custom) - Number(b.custom) || a.key.localeCompare(b.key)
  );
}

/** Ordered keys — what grouping and the Kanban columns iterate. */
export function visibleStatusKeys(
  catalogue: StatusCatalogue,
  projectId: number | null | undefined
): IssueStatus[] {
  return visibleStatuses(catalogue, projectId).map((d) => d.key);
}

/** Labels for every visible status, plus any extra key an issue actually holds. */
export function statusLabelMap(
  catalogue: StatusCatalogue,
  projectId: number | null | undefined,
  extraKeys: IssueStatus[] = []
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const d of visibleStatuses(catalogue, projectId)) out[d.key] = d.label;
  for (const key of extraKeys) {
    if (!(key in out)) out[key] = resolveStatus(catalogue, projectId, key).label;
  }
  return out;
}

/**
 * Is this live work? The one definition, mirroring the SQL `pm_status_is_open`.
 * An unknown key counts as open: a status nobody can resolve is more likely a
 * stale catalogue than finished work, and hiding live issues is the worse error.
 */
export function isOpenStatus(
  catalogue: StatusCatalogue,
  projectId: number | null | undefined,
  key: IssueStatus
): boolean {
  return resolveStatus(catalogue, projectId, key).category === "open";
}

/** Keys that mean finished — what the Overdue filter disables. */
export function doneStatusKeys(
  catalogue: StatusCatalogue,
  projectId: number | null | undefined
): IssueStatus[] {
  return visibleStatuses(catalogue, projectId)
    .filter((d) => d.category === "done")
    .map((d) => d.key);
}

/** Slugify a typed name into a key the DB will accept (`^[a-z0-9_]{1,40}$`). */
export function slugifyStatusKey(name: string, taken: Iterable<string>): string {
  const base =
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "status";
  const used = new Set(taken);
  // Built-in keys are refused by the DB, so never propose one.
  for (const k of BUILT_IN_ISSUE_STATUSES) used.add(k);
  if (!used.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const suffix = `_${n}`;
    const candidate = `${base.slice(0, 40 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${base.slice(0, 33)}_${Date.now().toString(36).slice(-6)}`;
}
